// ============================================================
// ReadoutNumber - a number that reads as text until it is touched.
//
// The sheet's replacement for a wall of input boxes: at rest the value
// is plain type, on hover it underlines, and while editing it is a field
// with a - and + beside it. The wheel and the arrow keys step it while
// it has focus (never on hover, or scrolling the sheet would change
// numbers), Enter leaves the field and Escape puts back the value it had
// when editing began.
//
// It IS a labelled <input type="number"> the whole time, on purpose: a
// screen reader and Playwright both find it by its label, and both can
// read and fill it without knowing it ever looked like text.
// ============================================================
import { useEffect, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import clsx from '../../utils/clsx';
import { clampNumber } from '../../utils/numberField';
import NumberInput from '../NumberInput';
import styles from './Sheet.module.css';

interface ReadoutNumberProps {
  /** The visible caption above the value; omit it and pass `ariaLabel` for a bare value. */
  label?: ReactNode;
  ariaLabel?: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  allowNegative?: boolean;
  float?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  title?: string;
  testId?: string;
}

const SIZE_CLASS = { small: styles.readoutSmall, medium: undefined, large: styles.readoutLarge };

/** The value moved by `delta` steps, kept inside the field's bounds. */
function stepped(p: ReadoutNumberProps, delta: number): number {
  const bounds = { min: p.min, max: p.max, allowNegative: p.allowNegative, float: p.float };
  return clampNumber(p.value + delta * (p.step ?? 1), bounds);
}

export default function ReadoutNumber(props: ReadoutNumberProps) {
  const { label, ariaLabel, value, onChange, disabled, size = 'medium', title, testId } = props;
  const before = useRef(value);
  const nudge = (delta: number) => onChange(stepped(props, delta));

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
    if (e.key !== 'Escape') return;
    // Stop here: the same key would otherwise close whatever the sheet sits in.
    e.stopPropagation();
    // The field keeps the focus: leaving it now would commit the text
    // still in it, over the value just put back.
    onChange(before.current);
  };
  const field = useWheelStep(nudge);

  return (
    <label className={styles.readout} title={title}>
      {label && <span className={styles.readoutLabel}>{label}</span>}
      <span className={styles.readoutField}>
        <NumberInput
          ref={field}
          mirrorWhileFocused
          className={clsx(styles.readoutInput, SIZE_CLASS[size])}
          aria-label={ariaLabel}
          data-testid={testId}
          value={value}
          onChange={onChange}
          min={props.min}
          max={props.max}
          step={props.step}
          allowNegative={props.allowNegative}
          float={props.float}
          disabled={disabled}
          onFocus={() => {
            before.current = value;
          }}
          onKeyDown={onKeyDown}
        />
        {/* After the input in the DOM so the label names the INPUT (a label
            names its first control); drawn before it by `order`. */}
        <StepButton label="Decrease" down disabled={disabled} onStep={() => nudge(-1)} />
        <StepButton label="Increase" disabled={disabled} onStep={() => nudge(1)} />
      </span>
    </label>
  );
}

/**
 * The wheel steps the field while it has focus; Shift steps by five.
 *
 * A native, non-passive listener: React's `onWheel` is passive, so it
 * cannot stop the browser stepping the number itself and scrolling the
 * sheet at the same time.
 */
function useWheelStep(nudge: (delta: number) => void) {
  const field = useRef<HTMLInputElement>(null);
  const latest = useRef(nudge);
  useEffect(() => {
    latest.current = nudge;
  });
  useEffect(() => {
    const input = field.current;
    if (!input) return;
    const onWheel = (e: WheelLike) => {
      if (document.activeElement !== input) return;
      e.preventDefault();
      latest.current(wheelSteps(e));
    };
    input.addEventListener('wheel', onWheel, { passive: false });
    return () => input.removeEventListener('wheel', onWheel);
  }, []);
  return field;
}

/** The part of a wheel event the stepper reads. */
interface WheelLike {
  deltaY: number;
  shiftKey: boolean;
  preventDefault: () => void;
}

/** Up is more, down is less; five at a time with Shift. */
function wheelSteps(e: WheelLike): number {
  return (e.deltaY < 0 ? 1 : -1) * (e.shiftKey ? 5 : 1);
}

interface StepButtonProps {
  label: string;
  /** The decrease button, drawn before the field. */
  down?: boolean;
  disabled?: boolean;
  onStep: () => void;
}

/**
 * A stepper beside the field, for the pointer only. Out of the tab order
 * and hidden from assistive tech: the field is a spinbutton, which the
 * keyboard already steps with the arrow keys - and a button inside the
 * label would otherwise be read as part of the field's NAME.
 */
function StepButton({ label, down, disabled, onStep }: StepButtonProps) {
  return (
    <button
      type="button"
      // The - and + are drawn by the stylesheet: as text they would sit inside
      // the label and become part of what the field is found by.
      className={clsx(styles.step, down ? styles.stepDown : styles.stepUp)}
      title={label}
      aria-hidden="true"
      tabIndex={-1}
      disabled={disabled}
      // Keep the focus in the field, or the steppers would vanish under the pointer.
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => {
        e.preventDefault();
        onStep();
      }}
    />
  );
}
